import type { z } from 'zod';

/** Where the extension shows up in the Contentstack UI. Maps 1:1 to the Extensions API `type`. */
export type ExtensionLocation = 'custom_field' | 'sidebar_widget' | 'dashboard_widget';

export type FieldDataType = 'text' | 'number' | 'boolean' | 'date' | 'json' | 'reference' | 'file';

export interface ExtensionMeta<C extends z.ZodTypeAny = z.ZodTypeAny> {
  /** Must equal the folder name under src/extensions/. Becomes the route and the stack-side tag. */
  id: string;
  title: string;
  description: string;
  /** GitHub team or handle that owns this extension. Used for CODEOWNERS and the index page. */
  owner: string;
  location: ExtensionLocation;
  /** custom_field only */
  dataType?: FieldDataType;
  multiple?: boolean;
  /**
   * Zod schema for the config params typed in the Contentstack UI.
   * Defaults declared here are what the sync script writes to the stack and what the
   * dev harness starts with. Invalid config fails loudly at runtime instead of silently.
   */
  config: C;
}

export type ConfigOf<M extends ExtensionMeta> = z.infer<M['config']>;

export function defineExtension<C extends z.ZodTypeAny>(meta: ExtensionMeta<C>): ExtensionMeta<C> {
  return meta;
}

/** Default config as the sync script and harness see it; {} when the schema has required fields. */
export function defaultConfig(meta: ExtensionMeta): Record<string, unknown> {
  const parsed = meta.config.safeParse({});
  return parsed.success ? (parsed.data as Record<string, unknown>) : {};
}
